import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { IoClose } from "react-icons/io5";
import { FaStar, FaRegStar } from "react-icons/fa";
import PhoneInputField from "@/Common/PhoneInputField";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { saveReview } from "@/services/api/common";
import { getReviewModuleOptions } from "@/utils/reviewHelpers";
import { isIndianUser } from "@/utils/getCountryCode";
import {
  buildReviewFormData,
  getReviewResponseMessage,
  validateReviewFiles,
  validateReviewForm,
} from "@/utils/reviewForm";
import { withRegionPath } from "@/utils/session";
import styles from "./WriteReviewModal.module.css";
import { TbVideoPlusFilled } from "react-icons/tb";
import { RiCameraAiFill } from "react-icons/ri";

const MAX_PHOTOS = 4;
const MAX_VIDEOS = 4;

const ERROR_FIELD_ORDER = [
  "moduleType",
  "userName",
  "phone",
  "emailId",
  "rating",
  "description",
  "recommend",
  "files",
];

const ERROR_FIELD_IDS = {
  moduleType: "review-module",
  userName: "review-name",
  phone: "review-phone",
  emailId: "review-email",
  rating: "review-rating",
  description: "review-text",
  recommend: "review-recommend",
  files: "review-upload",
};

function scrollToFirstError(nextErrors) {
  const firstKey = ERROR_FIELD_ORDER.find((key) => nextErrors[key]);
  if (!firstKey) return;

  const fieldId = ERROR_FIELD_IDS[firstKey];
  const fieldEl = fieldId ? document.getElementById(fieldId) : null;
  fieldEl?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export default function WriteReviewModal({
  open,
  onClose,
  moduleType,
  slug = "",
  onSuccess,
  redirectOnSuccess = false,
  allowModuleSelect = false,
  moduleOptions: moduleOptionsProp,
}) {
  const router = useRouter();
  const { showAlert } = useAlert();
  const { isDesktop } = useIsDesktop(900);
  const isIndia = isIndianUser();
  const moduleOptions = useMemo(
    () => moduleOptionsProp ?? getReviewModuleOptions(isIndia),
    [moduleOptionsProp, isIndia]
  );
  const showModuleSelect = allowModuleSelect && moduleOptions.length > 1;
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useBodyScrollLock(open);

  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [recommend, setRecommend] = useState(null);
  const [userName, setUserName] = useState("");
  const [emailId, setEmailId] = useState("");
  const [phone, setPhone] = useState(undefined);
  const [selectedModule, setSelectedModule] = useState(moduleType || "");
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setRating(0);
    setDescription("");
    setRecommend(null);
    setPhotos([]);
    setVideos([]);
    setUserName("");
    setEmailId("");
    setPhone(undefined);
    setSelectedModule(moduleType || "");
    setErrors({});
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (videoInputRef.current) videoInputRef.current.value = "";
  };
  useEffect(() => {
    if (!open) return;
    const defaultModule = showModuleSelect
      ? moduleType || moduleOptions[0]?.value || ""
      : moduleOptions[0]?.value || moduleType || "Holiday";
    setSelectedModule(defaultModule);
  }, [open, moduleType, moduleOptions, showModuleSelect]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const photoPreviewUrls = useMemo(
    () => photos.map((file) => URL.createObjectURL(file)),
    [photos]
  );

  const videoPreviewUrls = useMemo(
    () => videos.map((file) => URL.createObjectURL(file)),
    [videos]
  );

  const canAddPhotos = photos.length < MAX_PHOTOS;
  const canAddVideos = videos.length < MAX_VIDEOS;

  useEffect(() => {
    return () => {
      photoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photoPreviewUrls]);

  useEffect(() => {
    return () => {
      videoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [videoPreviewUrls]);

  if (!open) return null;

  const activeModule = showModuleSelect
    ? selectedModule
    : moduleOptions[0]?.value || moduleType || "Holiday";

  const handlePhotoUpload = (event) => {
    const incoming = Array.from(event.target.files || []);
    const combined = [...photos, ...incoming].slice(0, MAX_PHOTOS);
    const fileError = validateReviewFiles(combined, videos);

    if (fileError) {
      setErrors((prev) => ({ ...prev, files: fileError }));
      event.target.value = "";
      return;
    }

    setPhotos(combined);
    setErrors((prev) => ({ ...prev, files: "" }));
    event.target.value = "";
  };

  const handleVideoUpload = (event) => {
    const incoming = Array.from(event.target.files || []);
    const combined = [...videos, ...incoming].slice(0, MAX_VIDEOS);
    const fileError = validateReviewFiles(photos, combined);

    if (fileError) {
      setErrors((prev) => ({ ...prev, files: fileError }));
      event.target.value = "";
      return;
    }

    setVideos(combined);
    setErrors((prev) => ({ ...prev, files: "" }));
    event.target.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      if (!next.length && photoInputRef.current) {
        photoInputRef.current.value = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const removeVideo = (index) => {
    setVideos((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      if (!next.length && videoInputRef.current) {
        videoInputRef.current.value = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateReviewForm({
      userName,
      emailId,
      phone,
      rating,
      description,
      recommend,
      moduleType: activeModule,
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      scrollToFirstError(nextErrors);
      return;
    }

    const fileError = validateReviewFiles(photos, videos);
    if (fileError) {
      setErrors((prev) => ({ ...prev, files: fileError }));
      scrollToFirstError({ files: fileError });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const formData = buildReviewFormData({
        userName,
        emailId,
        phone,
        moduleType: activeModule,
        slug,
        rating,
        description,
        recommend,
        photos,
        videos,
      });

      const response = await saveReview(formData);

      if (redirectOnSuccess) {
        resetForm();
        onClose?.();
        router.push(withRegionPath("/thank-you?context=review"));
        return;
      }

      showAlert({
        title: "Review submitted",
        message:
          getReviewResponseMessage(
            response,
            "Your review has been submitted successfully."
          ),
      });

      resetForm();
      onClose?.();
      onSuccess?.(activeModule);
    } catch (error) {
      console.error("Submit review error:", error);
      showAlert({
        title: "Submission failed",
        message:
          getReviewResponseMessage(error?.response?.data) ||
          error?.message ||
          "Something went wrong while submitting. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${styles.root} ${isDesktop ? styles.rootDesktop : styles.rootMobile}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="write-review-title"
    >
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close write review dialog"
        onClick={handleClose}
      />

      <form
        className={`${styles.modal} ${isDesktop ? styles.modalDesktop : styles.modalMobile}`}
        onSubmit={handleSubmit}
        noValidate
        aria-busy={loading}
      >
        {loading ? (
          <div className={styles.loaderOverlay} role="status" aria-live="polite">
            <div className={styles.loaderContent}>
              <span className={styles.loaderSpinner} aria-hidden="true" />
              <p className={styles.loaderText}>Submitting your review...</p>
            </div>
          </div>
        ) : null}

        <div className={styles.header}>
          <div>
            <h2 id="write-review-title" className={styles.title}>
              Tell us about your experiences! 
            </h2>
            <p className={styles.subtitle}>
              Your feedback helps us make every journey better.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close"
            onClick={handleClose}
            disabled={loading}
          >
            <IoClose size={24} />
          </button>
        </div>

        <div className={styles.formBody}>
          {showModuleSelect ? (
            <label className={styles.fieldFull}>
              <span className={styles.fieldLabel}>Module</span>
              <select
                id="review-module"
                className={`${styles.input} ${styles.select} ${errors.moduleType ? styles.inputError : ""
                  }`}
                value={activeModule}
                disabled={loading}
                onChange={(event) => {
                  setSelectedModule(event.target.value);
                  setErrors((prev) => ({ ...prev, moduleType: "" }));
                }}
              >
                <option value="">Select Module</option>
                {moduleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.moduleType ? (
                <span className={styles.errorText}>{errors.moduleType}</span>
              ) : null}
            </label>
          ) : null}

          <label className={styles.fieldFull}>
            <span className={styles.fieldLabel}>Name</span>
            <input
              id="review-name"
              type="text"
              className={`${styles.input} ${errors.userName ? styles.inputError : ""}`}
              placeholder="Your Name"
              value={userName}
              disabled={loading}
              onChange={(event) => {
                setUserName(event.target.value);
                setErrors((prev) => ({ ...prev, userName: "" }));
              }}
            />
            {errors.userName ? (
              <span className={styles.errorText}>{errors.userName}</span>
            ) : null}
          </label>

          <div className={styles.formGrid}>
            <div className={styles.field} id="review-phone">
              <span className={styles.fieldLabel}>Phone Number</span>
              <PhoneInputField
                id="review-phone-input"
                name="phone"
                value={phone}
                onChange={(value) => {
                  setPhone(value);
                  setErrors((prev) => ({ ...prev, phone: "" }));
                }}
                error={Boolean(errors.phone)}
                className={styles.phoneField}
                placeholder="Enter mobile number"
                disabled={loading}
              />
              {errors.phone ? (
                <span className={styles.errorText}>{errors.phone}</span>
              ) : null}
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Email ID</span>
              <input
                id="review-email"
                type="email"
                className={`${styles.input} ${errors.emailId ? styles.inputError : ""}`}
                placeholder="Your Email"
                value={emailId}
                disabled={loading}
                onChange={(event) => {
                  setEmailId(event.target.value);
                  setErrors((prev) => ({ ...prev, emailId: "" }));
                }}
              />
              {errors.emailId ? (
                <span className={styles.errorText}>{errors.emailId}</span>
              ) : null}
            </label>
          </div>

          <div className={styles.fieldFull} id="review-rating">
            <span className={styles.fieldLabelStatic}>
              How was your overall experience? 
            </span>
            <div
              className={`${styles.starsRow} ${errors.rating ? styles.starsRowError : ""}`}
              role="group"
              aria-label="Rating"
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={styles.starBtn}
                  aria-label={`${value} star${value > 1 ? "s" : ""}`}
                  aria-pressed={value <= rating}
                  disabled={loading}
                  onClick={() => {
                    setRating(value);
                    setErrors((prev) => ({ ...prev, rating: "" }));
                  }}
                >
                  {value <= rating ? (
                    <FaStar color="#FFD200" size={28} />
                  ) : (
                    <FaRegStar color="#D9D9D9" size={28} />
                  )}
                </button>
              ))}
            </div>
            {errors.rating ? (
              <span className={styles.errorText}>{errors.rating}</span>
            ) : null}
          </div>

          <div className={styles.fieldFull}>
            <span className={styles.fieldLabelStatic}>Share your experience </span>
            <textarea
              id="review-text"
              className={`${styles.textarea} ${errors.description ? styles.inputError : ""}`}
              placeholder="Type here"
              value={description}
              disabled={loading}
              onChange={(event) => {
                setDescription(event.target.value);
                setErrors((prev) => ({ ...prev, description: "" }));
              }}
            />
            {errors.description ? (
              <span className={styles.errorText}>{errors.description}</span>
            ) : null}
          </div>

          <fieldset
            className={`${styles.recommendBox} ${errors.recommend ? styles.recommendBoxError : ""}`}
            id="review-recommend"
          >
            <legend className={styles.fieldLabelStatic}>
             Would you recommend us to others? 
            </legend>
            <div className={styles.radioRow}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="recommend"
                  className={styles.radioInput}
                  checked={recommend === true}
                  onChange={() => {
                    setRecommend(true);
                    setErrors((prev) => ({ ...prev, recommend: "" }));
                  }}
                />
                <span className={styles.radioCustom} />
                Yes
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="recommend"
                  className={styles.radioInput}
                  checked={recommend === false}
                  onChange={() => {
                    setRecommend(false);
                    setErrors((prev) => ({ ...prev, recommend: "" }));
                  }}
                />
                <span className={styles.radioCustom} />
                No
              </label>
            </div>
            {errors.recommend ? (
              <span className={styles.errorText}>{errors.recommend}</span>
            ) : null}
          </fieldset>

          <div className={styles.uploadSection} id="review-upload">
            <span className={styles.fieldLabelStatic}>Add photos &amp; videos</span>
            <div
              className={`${styles.uploadRow} ${errors.files ? styles.uploadRowError : ""} ${
                !canAddPhotos || !canAddVideos ? styles.uploadRowSingle : ""
              }`}
            >
              {canAddPhotos ? (
                <>
                  <button
                    type="button"
                    className={styles.uploadBox}
                    disabled={loading}
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <RiCameraAiFill size={30} color="#D92027" aria-hidden="true" />
                    <div className={styles.uploadTextGroup}>
                      <p className={styles.uploadTitle}>Tap to add photos</p>
                      <p className={styles.uploadHint}>(upto 4 at once)</p>
                      {photos.length > 0 ? (
                        <p className={styles.uploadCount}>{photos.length} photo(s) added</p>
                      ) : null}
                    </div>
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={photoInputRef}
                    className={styles.hiddenInput}
                    onChange={handlePhotoUpload}
                  />
                </>
              ) : null}

              {canAddVideos ? (
                <>
                  <button
                    type="button"
                    className={styles.uploadBox}
                    disabled={loading}
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <TbVideoPlusFilled size={30} color="#D92027" aria-hidden="true" />
                    <div className={styles.uploadTextGroup}>
                      <p className={styles.uploadTitle}>Tap to add videos</p>
                      <p className={styles.uploadHint}>(upto 4 at once)</p>
                      {videos.length > 0 ? (
                        <p className={styles.uploadCount}>
                          {videos.length} video{videos.length === 1 ? "" : "s"} added
                        </p>
                      ) : null}
                    </div>
                  </button>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    ref={videoInputRef}
                    className={styles.hiddenInput}
                    onChange={handleVideoUpload}
                  />
                </>
              ) : null}
            </div>
            {photos.length > 0 || videos.length > 0 ? (
              <div className={styles.previewSection}>
                {photos.length > 0 ? (
                  <div className={styles.previewGroup}>
                    <p className={styles.previewLabel}>Photos</p>
                    <div className={styles.previewGrid}>
                      {photos.map((file, index) => (
                        <div key={`${file.name}-${file.lastModified}-${index}`} className={styles.previewItem}>
                          <img
                            src={photoPreviewUrls[index]}
                            alt={file.name || `Uploaded photo ${index + 1}`}
                            className={styles.previewThumb}
                          />
                          <button
                            type="button"
                            className={styles.removeBtn}
                            aria-label={`Remove photo ${index + 1}`}
                            disabled={loading}
                            onClick={() => removePhoto(index)}
                          >
                            <IoClose size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {videos.length > 0 ? (
                  <div className={styles.previewGroup}>
                    <p className={styles.previewLabel}>
                      {videos.length === 1 ? "Video" : "Videos"}
                    </p>
                    <div className={styles.previewGrid}>
                      {videos.map((file, index) => (
                        <div
                          key={`${file.name}-${file.lastModified}-${index}`}
                          className={styles.previewItem}
                        >
                          <video
                            src={videoPreviewUrls[index]}
                            className={styles.previewVideo}
                            muted
                            playsInline
                            preload="metadata"
                          />
                          <button
                            type="button"
                            className={styles.removeBtn}
                            aria-label={`Remove video ${index + 1}`}
                            disabled={loading}
                            onClick={() => removeVideo(index)}
                          >
                            <IoClose size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {errors.files ? (
              <span className={styles.errorText}>{errors.files}</span>
            ) : null}
          </div>
        </div>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
}